import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import apiClient from '../../services/api';
import './TestPublish.css';

const PLATFORMS = [
  { id: 'instagram', label: 'Instagram', icon: '📸' },
  { id: 'facebook', label: 'Facebook', icon: '📘' },
  { id: 'tiktok', label: 'TikTok', icon: '🎵' },
];

function TestPublishManual() {
  const { t } = useTranslation();
  const [videoUrl, setVideoUrl] = useState('');
  const [caption, setCaption] = useState('');
  const [platform, setPlatform] = useState('instagram');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const trimmedUrl = videoUrl.trim();
  const urlIsHttp = trimmedUrl.startsWith('http://') || trimmedUrl.startsWith('https://');
  const canSubmit = trimmedUrl.length >= 10 && urlIsHttp && !!platform && !submitting;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;

    setSubmitting(true);
    setResult(null);
    try {
      const response = await apiClient.post('/api/v1/admin/test-publish/manual', {
        video_url: trimmedUrl,
        caption,
        platform,
      });
      setResult({
        type: 'success',
        message: response.data.message,
        postId: response.data.post_id,
        platform: response.data.platform,
      });
    } catch (err) {
      setResult({
        type: 'error',
        message: err.response?.data?.detail || t('testPublish.publishError', 'Publish failed'),
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = () => {
    setVideoUrl('');
    setCaption('');
    setPlatform('instagram');
    setResult(null);
  };

  return (
    <div className="test-publish-page">
      <div className="page-header">
        <div>
          <h1>🧪 Manual Test Publish</h1>
          <p className="page-subtitle">
            Pubblica un post ad-hoc su un singolo social, fornendo manualmente
            video URL e caption. Crea un ScheduledPost temporaneo nel batch
            "manual_tests" del superadmin loggato e lancia il task di
            pubblicazione standard.
          </p>
        </div>
      </div>

      <div className="warning-banner">
        <span className="warning-icon">⚠️</span>
        <div>
          <strong>Solo superadmin.</strong>
          <p>
            Il post verrà pubblicato realmente sul tuo account social — non è
            una simulazione. L'esito (status, permalink, errore) sarà visibile
            nella pagina <em>Test Publish</em> con il post-id mostrato qui sotto.
          </p>
        </div>
      </div>

      {result && (
        <div className={`result-toast ${result.type}`}>
          {result.type === 'success' ? '✅' : '❌'} {result.message}
          {result.type === 'success' && result.postId && (
            <span style={{ marginLeft: 8 }}>(post #{result.postId})</span>
          )}
        </div>
      )}

      <form className="manual-publish-form" onSubmit={handleSubmit}>
        <div className="form-section">
          <label htmlFor="manual-video-url">
            <strong>Video URL</strong> <span className="required">*</span>
          </label>
          <input
            id="manual-video-url"
            type="url"
            placeholder="https://video-api.piirz.com/api/v1/videos/<id>/stream"
            value={videoUrl}
            onChange={(e) => setVideoUrl(e.target.value)}
            disabled={submitting}
            required
          />
          {trimmedUrl && !urlIsHttp && (
            <small className="form-hint error">
              L'URL deve iniziare con http:// o https://
            </small>
          )}
          {urlIsHttp && (
            <small className="form-hint">
              Lunghezza: {trimmedUrl.length} caratteri
            </small>
          )}
        </div>

        <div className="form-section">
          <label htmlFor="manual-caption">
            <strong>Caption</strong>
          </label>
          <textarea
            id="manual-caption"
            placeholder="Testo del post (opzionale). Hashtag inclusi nel testo."
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            disabled={submitting}
            rows={6}
            maxLength={4000}
          />
          <small className="form-hint">
            {caption.length} / 4000 caratteri
          </small>
        </div>

        <div className="form-section">
          <label>
            <strong>Piattaforma</strong> <span className="required">*</span>
          </label>
          <div className="platform-radio-group">
            {PLATFORMS.map((p) => (
              <label
                key={p.id}
                className={`platform-radio ${platform === p.id ? 'selected' : ''}`}
              >
                <input
                  type="radio"
                  name="platform"
                  value={p.id}
                  checked={platform === p.id}
                  onChange={() => setPlatform(p.id)}
                  disabled={submitting}
                />
                <span className="platform-radio-icon">{p.icon}</span>
                <span className="platform-radio-label">{p.label}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="form-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleReset}
            disabled={submitting}
          >
            Reset
          </button>
          <button
            type="submit"
            className="btn btn-publish"
            disabled={!canSubmit}
          >
            {submitting ? '⏳ Pubblicando…' : '🚀 Test Publish'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default TestPublishManual;
